import React, { useState } from 'react';
import axios from 'axios';
import { VertragVersion } from '../types';
import { euro, periodenSumme, betrag } from '../lib/vertrag';
import { fehlerText } from '../lib/belegKarte';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Zeile {
  key: number;
  artikel_id: string;
  beschreibung: string;
  einheit: string | null;
  menge: string;
  preis_einzeln: string;
  rabatt_prozent: string;
  steuersatz: string | null;
  steuer_kategorie: string | null;
}

interface Props {
  vertragId: number;
  /** Vorbelegung „gültig ab“: nächster Abrechnungsstichtag. */
  gueltigAbVorschlag: string | null;
  aktuelleVersion: VertragVersion | null;
  onGespeichert: () => void;
  onAbbrechen: () => void;
}

const zahlText = (v: string | null) => (v == null || v === '' ? '' : String(betrag(v)));

/** Dialog „Neue Version“: Positionen der aktuellen Version vorbelegt, editierbar,
 *  Live-Summe je Periode → POST /api/vertraege/{id}/versionen. */
const VertragVersionDialog: React.FC<Props> = ({
  vertragId, gueltigAbVorschlag, aktuelleVersion, onGespeichert, onAbbrechen,
}) => {
  const [gueltigAb, setGueltigAb] = useState(gueltigAbVorschlag ?? '');
  const [grund, setGrund] = useState('');
  const [zeilen, setZeilen] = useState<Zeile[]>(() =>
    (aktuelleVersion?.positionen ?? []).map((p, i) => ({
      key: i,
      artikel_id: p.artikel_id != null ? String(p.artikel_id) : '',
      beschreibung: p.beschreibung ?? '',
      einheit: p.einheit,
      menge: zahlText(p.menge),
      preis_einzeln: zahlText(p.preis_einzeln),
      rabatt_prozent: zahlText(p.rabatt_prozent),
      steuersatz: p.steuersatz,
      steuer_kategorie: p.steuer_kategorie,
    })),
  );
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const setze = (key: number, feld: keyof Zeile, wert: string) =>
    setZeilen(zs => zs.map(z => (z.key === key ? { ...z, [feld]: wert } : z)));
  const entferne = (key: number) => setZeilen(zs => zs.filter(z => z.key !== key));
  const neueZeile = () =>
    setZeilen(zs => [...zs, {
      key: Math.max(-1, ...zs.map(z => z.key)) + 1, artikel_id: '', beschreibung: '', einheit: null,
      menge: '1', preis_einzeln: '', rabatt_prozent: '', steuersatz: null, steuer_kategorie: null,
    }]);

  const summe = periodenSumme(zeilen.map(z => ({
    menge: z.menge, preis_einzeln: z.preis_einzeln, rabatt_prozent: z.rabatt_prozent || null,
  })));

  const speichern = async () => {
    setFehler(null);
    if (!gueltigAb) { setFehler('Bitte „gültig ab“ angeben.'); return; }
    if (grund.trim().length < 3) { setFehler('Bitte einen Grund angeben (mindestens 3 Zeichen).'); return; }
    if (zeilen.length === 0) { setFehler('Mindestens eine Position ist nötig.'); return; }
    if (zeilen.some(z => !(Number(z.artikel_id) > 0) || !(Number(z.menge) > 0) || z.preis_einzeln === '')) {
      setFehler('Jede Position braucht Artikel-ID, Menge > 0 und Einzelpreis.');
      return;
    }
    setSpeichert(true);
    try {
      await axios.post(`${API_URL}/api/vertraege/${vertragId}/versionen`, {
        gueltig_ab: gueltigAb,
        grund: grund.trim(),
        positionen: zeilen.map(z => ({
          artikel_id: Number(z.artikel_id),
          beschreibung: z.beschreibung.trim() || null,
          einheit: z.einheit,
          menge: Number(z.menge),
          preis_einzeln: Number(z.preis_einzeln),
          rabatt_prozent: z.rabatt_prozent === '' ? null : Number(z.rabatt_prozent),
          steuersatz: z.steuersatz,
          steuer_kategorie: z.steuer_kategorie,
        })),
      });
      onGespeichert();
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setFehler(fehlerText(detail, 'Version konnte nicht angelegt werden.'));
    } finally {
      setSpeichert(false);
    }
  };

  const zahl: React.CSSProperties = { width: 90, padding: 4, textAlign: 'right' };

  return (
    <div className="aa-rechnung-dialog">
      <div className="aa-rechnung-dialog__zeile">
        <label>Gültig ab <input type="date" value={gueltigAb} onChange={e => setGueltigAb(e.target.value)} /></label>
        <input
          placeholder="Grund der Änderung *"
          value={grund}
          onChange={e => setGrund(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: 4 }}
        />
      </div>
      <table className="aa-positionen-table">
        <thead>
          <tr>
            <th>Artikel-ID</th><th>Beschreibung</th><th>Menge</th><th>Einzelpreis</th><th>Rabatt %</th><th></th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map(z => (
            <tr key={z.key}>
              <td>
                <input type="number" min={1} value={z.artikel_id} style={{ width: 80, padding: 4 }}
                       onChange={e => setze(z.key, 'artikel_id', e.target.value)} />
              </td>
              <td>
                <input value={z.beschreibung} placeholder="aus Artikel" style={{ width: '100%', padding: 4 }}
                       onChange={e => setze(z.key, 'beschreibung', e.target.value)} />
              </td>
              <td>
                <input type="number" min={0} step="any" value={z.menge} style={zahl}
                       onChange={e => setze(z.key, 'menge', e.target.value)} />
              </td>
              <td>
                <input type="number" min={0} step="0.01" value={z.preis_einzeln} style={zahl}
                       onChange={e => setze(z.key, 'preis_einzeln', e.target.value)} />
              </td>
              <td>
                <input type="number" min={0} max={100} step="0.01" value={z.rabatt_prozent} style={zahl}
                       onChange={e => setze(z.key, 'rabatt_prozent', e.target.value)} />
              </td>
              <td>
                <button type="button" className="aa-mini-btn" onClick={() => entferne(z.key)}>Entfernen</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="aa-rechnung-dialog__zeile">
        <button type="button" className="aa-mini-btn" onClick={neueZeile}>+ Position</button>
        <span style={{ marginLeft: 'auto', fontWeight: 600 }}>Summe je Periode: {euro(summe)}</span>
      </div>
      {fehler && <div className="aa-inline-fehler">{fehler}</div>}
      <div className="aa-rechnung-dialog__zeile">
        <button type="button" className="k-btn k-btn--primary" onClick={speichern} disabled={speichert}>
          {speichert ? 'Speichert…' : 'Version anlegen'}
        </button>
        <button type="button" className="k-btn k-btn--ghost" onClick={onAbbrechen} disabled={speichert}>
          Abbrechen
        </button>
      </div>
    </div>
  );
};

export default VertragVersionDialog;
